import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './BuComparison.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';
import { useBusinessUnits } from '../../api/useBusinessUnits';
import { useBuSummary } from '../../api/useBuSummary';

function formatBuCount(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return String(n);
}

function Pair({ total, extra, extraClass }) {
    return (
        <>
            <span className="xbu-num">{formatBuCount(total)}</span>
            <span className={`xbu-split ${extraClass || ''}`}>{formatBuCount(extra)}</span>
        </>
    );
}

// One row per metric. `path` is the screen a cell opens; the BU whose column was
// clicked becomes the working BU first, so that screen loads scoped to it.
// Critical PII has no dedicated screen yet, so it lands on Data Extensions.
const METRICS = [
    {
        key: 'des', label: 'Data Extensions', path: '/data-extensions',
    },
    {
        key: 'automations', label: 'Automations', note: 'total / erroring', path: '/automations',
        render: s => <Pair total={s.automations} extra={s.autoError}
                           extraClass={s.autoError ? 'is-alert' : ''} />,
    },
    {
        key: 'journeys', label: 'Journeys', note: 'total / active', path: '/journeys',
        render: s => <Pair total={s.journeys} extra={s.journeysActive} extraClass="is-ok" />,
    },
    {
        key: 'sendable', label: 'Sendable DEs', path: '/data-extensions',
    },
    {
        key: 'criticalPii', label: 'Critical PII DEs', path: '/data-extensions', alert: true,
    },
    {
        key: 'subscribers', label: 'Active subscribers', path: '/subscriber-health',
    },
    {
        key: 'hardBounce', label: 'Hard bounced', path: '/subscriber-health', alert: true,
    },
    {
        key: 'sharedDes', label: 'Shared DEs', path: '/data-extensions',
    },
];

function BuComparison() {
    usePageTitle('Cross-BU Comparison');

    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    const {
        units:   allUnits,
        loading: buLoading,
        error:   buError,
        selectedBu,
        selectBu,
    } = useBusinessUnits();

    const {
        metrics,
        loading: metricsLoading,
        error:   metricsError,
    } = useBuSummary();

    const units = useMemo(() => {
        const hasParent = allUnits.some(u => u.parent);
        return allUnits.map((unit, index) => ({
            ...unit,
            // The API flags the parent via defaultMid; older cached lists fall back
            // to the first entry, which is the parent account.
            parent: hasParent ? unit.parent === true : index === 0,
            // /comparison/summary is keyed by MID; a BU it doesn't report renders “—”.
            stats:  metrics[unit.mid] || metrics[unit.id] || {},
        }));
    }, [allUnits, metrics]);

    // Parent first so its column pins leftmost; the filter only narrows the rest.
    const visibleUnits = useMemo(() => {
        const q = query.trim().toLowerCase();
        const parent   = units.find(u => u.parent);
        const children = units.filter(u => !u.parent && (
            !q || u.name.toLowerCase().includes(q) || (u.mid && u.mid.includes(q))
        ));
        return [parent, ...children].filter(Boolean);
    }, [units, query]);

    const totalError = units.reduce(
        (sum, u) => sum + Number(u.stats.autoError || 0), 0
    );
    const totalCritical = units.reduce(
        (sum, u) => sum + Number(u.stats.criticalPii || 0), 0
    );
    const parentUnit = units.find(u => u.parent);

    // Switching the BU before navigating means the destination mounts already
    // scoped to it — BuScoped in App.js keys on the selection.
    const openMetric = (unit, metric) => {
        selectBu(unit);
        navigate(metric.path);
    };

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-3">
                    <h1>Cross-BU Comparison</h1>
                    <p>Compare metrics across every active business unit. The parent BU column stays pinned on the left.</p>
                </div>

                <div className="xbu-chips mb-4">
                    <span className="xbu-chip"><strong>{units.length}</strong> active BUs</span>
                    {parentUnit && (
                        <span className="xbu-chip xbu-chip-indigo">
                            <strong>{parentUnit.name}</strong> parent
                        </span>
                    )}
                    <span className="xbu-chip xbu-chip-amber">
                        <strong>{metricsLoading ? '—' : totalError}</strong> erroring automations
                    </span>
                    <span className="xbu-chip xbu-chip-rose">
                        <strong>{metricsLoading ? '—' : totalCritical}</strong> critical PII DEs
                    </span>
                </div>

                <div className="xbu-card">
                    <div className="xbu-toolbar">
                        <div>
                            <h2 className="xbu-title">All active BUs</h2>
                            <p className="xbu-sub">Click any figure to open that screen for the business unit</p>
                        </div>
                        <div className="search-wrapper xbu-search">
                            <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24"
                                 fill="none" stroke="#94A3B8" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Filter business units…"
                            />
                        </div>
                    </div>

                    <p className="xbu-swipe-hint">Swipe to see more business units</p>

                    {/* The matrix still renders on a metrics failure — every cell just shows “—”. */}
                    {metricsError && (
                        <p className="xbu-empty">Couldn’t load BU metrics: {metricsError}</p>
                    )}

                    {visibleUnits.length === 0 ? (
                        <p className="xbu-empty">
                            {buLoading
                                ? 'Loading business units…'
                                : buError
                                    ? `Couldn’t load business units: ${buError}`
                                    : units.length === 0
                                        ? 'No business units available for this account.'
                                        : `No business units match “${query}”.`}
                        </p>
                    ) : (
                        <div className="xbu-scroll">
                            <table className="xbu-table xbu-matrix">
                                <thead>
                                    <tr>
                                        <th scope="col" className="xbu-metric-col">Metric</th>
                                        {visibleUnits.map(unit => {
                                            const initial = unit.name.replace(/^iRobot_/, '').charAt(0).toUpperCase();
                                            const classes = [
                                                'xbu-bu-col',
                                                unit.parent ? 'is-parent-col' : '',
                                                selectedBu && unit.id === selectedBu.id ? 'is-selected' : '',
                                            ].filter(Boolean).join(' ');

                                            return (
                                                <th key={unit.id} scope="col" className={classes}
                                                    title={unit.mid ? `${unit.name} · MID ${unit.mid}` : unit.name}>
                                                    <span className="xbu-bu">
                                                        <span className="xbu-dot">{initial}</span>
                                                        <span>
                                                            <span className="xbu-col-name">{unit.name}</span>
                                                            <span className="xbu-col-mid">
                                                                {unit.mid ? `MID ${unit.mid}` : 'MID —'}
                                                                {unit.parent ? ' · Parent' : ''}
                                                            </span>
                                                        </span>
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {METRICS.map(metric => (
                                        <tr key={metric.key}>
                                            <th scope="row" className="xbu-metric-col" title={metric.label}>
                                                <span className="xbu-metric-name">{metric.label}</span>
                                                {metric.note && (
                                                    <span className="xbu-th-note">{metric.note}</span>
                                                )}
                                            </th>

                                            {visibleUnits.map(unit => {
                                                const s     = unit.stats;
                                                const value = s[metric.key];
                                                const classes = [
                                                    'xbu-bu-col',
                                                    unit.parent ? 'is-parent-col' : '',
                                                    selectedBu && unit.id === selectedBu.id ? 'is-selected' : '',
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <td key={unit.id} className={classes}>
                                                        <button
                                                            type="button"
                                                            className="xbu-cell"
                                                            onClick={() => openMetric(unit, metric)}
                                                            title={`Open ${metric.label} for ${unit.name}`}
                                                        >
                                                            {metric.render
                                                                ? metric.render(s)
                                                                : (
                                                                    <span className={`xbu-num${metric.alert && value ? ' is-alert' : ''}`}>
                                                                        {formatBuCount(value)}
                                                                    </span>
                                                                )}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default BuComparison;
